<template>
    <div class="bg-FormSection p-2 text-center mb-2" v-if="isViewDetailsRequested">
        <i class="fw-bold">View in Progress</i>
    </div>
    <h6 v-if="isViewDetailsRequested" class="fw-bold mb-2">Hospitalizations/Emergency Department Encounters</h6>
    <FormSection :full-height="false" :application="application">
        <template #content>
            <section class="p-5 d-flex flex-column text-center text-primary" v-if="isLoading">
                <i class="far fa-save fa-7x me-2 animated slow flash infinite"></i>
                <div class="big-text mt-1">
                    <b>Save In Progress...</b>
                </div>
            </section>
            <section class="col-12 ps-2" v-else>
                <div class="row p-2 mb-0">
                    <div class="form-group col-md-3">
                        <label for="hospitalization-admission-date">
                            Admission Date
                            <span class="text-danger">*</span>
                        </label>
                        <DatePicker :getDate="handleDateChange">
                            <div class="input-group date datepickerInput">
                                <input
                                    type="text"
                                    name="hospitalization-admission-date"
                                    class="form-control"
                                    :value="formatDateInput(hospitalization.AdmissionDate)"
                                    placeholder="MM/DD/YYYY"
                                    autocomplete="off"
                                    :disabled="isViewDetailsRequested"
                                    @change="handleDateInputChange"
                                    @blur="v$.hospitalization.AdmissionDate.$touch"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                                <span v-if="v$.hospitalization.AdmissionDate.$error" class="field-invalid"
                                    >Admission Date is required.</span
                                >
                            </div>
                        </DatePicker>
                    </div>
                    <div class="form-group col-md-3">
                        <label for="hospitalization-discharge-date">
                            Discharge Date
                            <span
                                v-tooltip="{
                                    title: `To add a discharge date, change the patient's status back to Active.`
                                }"
                                v-if="patientInfo.isHospitalized && hospitalization.IsStatusUpdateData"
                            >
                                <i class="fas fa-info-circle text-info"></i>
                            </span>
                        </label>
                        <DatePicker :getDate="handleDateChange">
                            <div class="input-group date datepickerInput">
                                <input
                                    type="text"
                                    name="hospitalization-discharge-date"
                                    class="form-control"
                                    :value="formatDateInput(hospitalization.DischargeDate)"
                                    placeholder="MM/DD/YYYY"
                                    autocomplete="off"
                                    :disabled="
                                        (patientInfo.isHospitalized && hospitalization.IsStatusUpdateData) ||
                                        isViewDetailsRequested
                                    "
                                    @change="handleDateInputChange"
                                />
                                <div class="input-group-addon input-group-append">
                                    <div class="input-group-text">
                                        <i class="fa fa-calendar"></i>
                                    </div>
                                </div>
                            </div>
                        </DatePicker>
                    </div>
                    <div class="form-check col-md-2 mt-4 ms-1">
                        <input
                            type="checkbox"
                            class="form-check-input"
                            :id="`ed-encounter-${hospitalization.Id}`"
                            v-model="hospitalization.IsEdEncounter"
                            :disabled="isViewDetailsRequested"
                        />
                        <label class="form-check-label" :for="`ed-encounter-${hospitalization.Id}`">
                            <span>Emergency Department Encounter</span>
                        </label>
                    </div>
                    <div class="form-group col-12 col-md-3">
                        <label for="hospital-unit">Hospital Unit</label>
                        <input
                            class="form-control"
                            type="text"
                            placeholder="Enter Hospital Unit"
                            v-model="hospitalization.HospitalUnit"
                            :disabled="isViewDetailsRequested"
                        />
                    </div>
                    <div class="form-group col-12 col-lg-2 d-flex">
                        <div class="form-group form-check custom-checkbox">
                            <input
                                type="checkbox"
                                class="form-check-input"
                                id="is-planned"
                                :value="true"
                                v-model="hospitalization.IsPlanned"
                                :disabled="isViewDetailsRequested"
                            />
                            <label class="form-check-label me-4" for="is-planned">
                                <span>Planned</span>
                            </label>
                        </div>
                        <div class="form-group form-check custom-checkbox">
                            <input
                                type="checkbox"
                                class="form-check-input"
                                id="is-preventable"
                                :value="true"
                                v-model="hospitalization.IsPreventable"
                                :disabled="isViewDetailsRequested"
                            />
                            <label class="form-check-label me-4" for="is-preventable">
                                <span>Preventable</span>
                            </label>
                        </div>
                    </div>
                </div>
                <div class="row p-2 form-group mb-0">
                    <div class="form-group col-12 col-lg-3 pe-lg-3">
                        <label for="facility-name">
                            <span>Facility Name</span>
                        </label>
                        <TypeaheadInput
                            :getData="getVendorList"
                            :value="selectedHospitalizedFacility"
                            v-on:valueChanged="handleSelectedFacilityChange"
                            placeholder="Enter Facility Name..."
                            :debounce="100"
                            :disabled="isViewDetailsRequested"
                        >
                        </TypeaheadInput>
                    </div>
                    <div class="form-group col-12 col-lg-4 pe-lg-3">
                        <label for="facility-contact-name">
                            <span>Facility Point of Contact Name</span>
                        </label>
                        <input
                            class="form-control"
                            maxlength="50"
                            type="text"
                            placeholder="Enter Name"
                            v-model="hospitalization.FacilityContactName"
                            :disabled="isViewDetailsRequested"
                        />
                    </div>
                    <div class="form-group col-12 col-lg-3 pe-lg-2">
                        <label for="community-liaison"> Community Liaison </label>
                        <TypeaheadInput
                            :getData="getUsersByRole"
                            :value="selectedCommunityLiaison"
                            v-on:valueChanged="handleSelectedCommunityLiaison"
                            init-:value="selectedCommunityLiaison"
                            placeholder="Enter Community Liaison..."
                            :disabled="isViewDetailsRequested"
                        ></TypeaheadInput>
                    </div>
                </div>
            </section>
            <section class="col-12 ps-2">
                <div class="form-group p-2 col-md-12">
                    <label :for="`discharge-diagnosis-${hospitalization.Id}`"> Discharge Diagnoses </label>
                    <div class="row col-12">
                        <div class="col-md-11">
                            <DiagnosisTable
                                :diagnosis="hospitalization.HospitalizationHistoryDiagnoses"
                                :updateDiagnosis="handleUpdateDiagnosis"
                                :limit="diagnosisLimit"
                                :history-view="true"
                                :hide-resolved="true"
                                :is-view-details-requested="isViewDetailsRequested"
                                :application="application"
                            ></DiagnosisTable>
                        </div>
                        <div class="form-check mx-auto ps-0 col-md-1">
                            <input
                                type="checkbox"
                                class="mx-1"
                                :id="`unknown-diagnosis-${hospitalization.Id}`"
                                v-model="isDiagnosisUnknown"
                                :disabled="isViewDetailsRequested"
                            />
                            <label class="form-check-label" :for="`unknown-diagnosis-${hospitalization.Id}`">
                                <span>Unknown</span>
                            </label>
                        </div>
                    </div>
                </div>
            </section>
            <section class="col-12 px-3">
                <div class="form-group col-md-12">
                    <label for="discharged-to"> Discharged To </label>
                    <input
                        type="text"
                        class="form-control"
                        maxlength="50"
                        v-model="hospitalization.DischargedTo"
                        :id="`discharged-to-${hospitalization.Id}`"
                        placeholder="Enter Location"
                        :disabled="!hospitalization.DischargeDate || isViewDetailsRequested"
                    />
                </div>
                <div class="form-group col-md-12">
                    <label for="comments"> Comments </label>
                    <textarea
                        class="form-control"
                        :id="`comments-${hospitalization.Id}`"
                        rows="4"
                        :maxlength="maxLength"
                        placeholder="Enter Comments"
                        v-model="hospitalization.Comments"
                        :disabled="isViewDetailsRequested"
                    ></textarea>
                    <p :class="`mb-0 ${maxLength == hospitalization.Comments.length ? 'text-danger' : ''}`">
                        You have {{ maxLength - hospitalization.Comments.length }} remaining characters.
                    </p>
                </div>
            </section>
        </template>
    </FormSection>
    <div class="col-md-12 d-flex justify-content-center align-items-center pb-2 mt-2" v-if="!isLoading">
        <button type="button" class="btn btn-primary btn-md me-2" v-if="!isViewDetailsRequested" @click="save()">
            Save
        </button>
        <button type="button" class="btn btn-outline-primary btn-md" v-if="!isViewDetailsRequested" @click="cancel()">
            Cancel
        </button>
        <button type="button" class="btn btn-primary btn-md mt-2" v-if="isViewDetailsRequested" @click="close()">
            Close
        </button>
    </div>
</template>

<script lang="ts" src="./HospitalAdmissionForm.ts" />
